import { useState, useEffect } from 'react';
import { useSEO } from '../../hooks/useSEO';
import { useClienteSession } from '../../hooks/useClienteSession';
import { useAdminClientesAsesoria } from '../../hooks/useAdminClientesAsesoria';
import Sidebar, { type BackofficeTab } from './components/Sidebar';
import Dashboard from './components/Dashboard';
import PerfilEstilistico from './components/PerfilEstilistico';
import Armario from './components/Armario';
import Calendario from './components/Calendario';
import Historial from './components/Historial';
import Combinaciones from './components/Combinaciones';
import LooksSugeridos from './components/LooksSugeridos';
import { useClienteArmario } from '../../hooks/useClienteArmario';
import { useClienteCalendario } from '../../hooks/useClienteCalendario';
import { useClienteRecomendaciones } from '../../hooks/useClienteRecomendaciones';
import { mockPrendas, mockLooksCalendario, mockRecomendaciones } from '../../mocks/clienteArmario';

export default function ClienteBackoffice() {
  const [activeTab, setActiveTab] = useState<BackofficeTab>('dashboard');
  const { clienteId, setClienteId } = useClienteSession();
  const { data: clientesData } = useAdminClientesAsesoria();

  // Auto-select first client if none selected
  useEffect(() => {
    if (clienteId === 0 && clientesData.length > 0) {
      setClienteId(clientesData[0].id);
    }
  }, [clienteId, clientesData, setClienteId]);

  const {
    data: prendas,
    add: addPrenda,
    update: updatePrenda,
    remove: removePrenda,
    uploadImage,
    loading: loadingPrendas,
  } = useClienteArmario(mockPrendas, clienteId || undefined);

  const {
    data: calendario,
    add: addLook,
    update: updateLook,
    remove: removeLook,
    loading: loadingCalendario,
    error: errorCalendario,
  } = useClienteCalendario(mockLooksCalendario, clienteId || undefined);

  const {
    data: recomendaciones,
    markAsRead,
    loading: loadingRecomendaciones,
  } = useClienteRecomendaciones(mockRecomendaciones, clienteId || undefined);

  const currentCliente = clientesData.find((c) => c.id === clienteId) ?? null;

  useSEO({
    title: currentCliente
      ? `Mi Asesoría | ${currentCliente.nombre}`
      : 'Mi Asesoría | Anthony Sánchez Couture',
    description: 'Espacio exclusivo para clientes de asesoría de imagen personal. Gestiona tu armario, planifica looks y recibe recomendaciones.',
    robots: 'noindex, nofollow',
  });

  // Listen for navigation events from Dashboard links
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail === 'armario') setActiveTab('armario');
      if (detail === 'calendario') setActiveTab('calendario');
      if (detail === 'combinaciones') setActiveTab('combinaciones');
      if (detail === 'historial') setActiveTab('historial');
      if (detail === 'looks-sugeridos') setActiveTab('looks-sugeridos');
    };
    window.addEventListener('navigate-backoffice', handler);
    return () => window.removeEventListener('navigate-backoffice', handler);
  }, []);

  // Badges: looks pendientes de foto o valoración
  const looksUsados = calendario.filter((c) => c.estado === 'usado');
  const looksSinFoto = looksUsados.filter((c) => !c.foto_real_url).length;
  const looksSinValorar = looksUsados.filter((c) => !c.calificacion).length;
  const historialBadge = looksSinFoto + looksSinValorar;

  const sidebarBadges: Partial<Record<BackofficeTab, number>> = {
    historial: historialBadge > 0 ? historialBadge : undefined,
  };

  if (clienteId === 0) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-gray-400 mb-4">
            <i className="ri-user-search-line text-2xl" />
          </div>
          <h1 className="font-serif text-brand-black text-2xl font-medium mb-2">
            No hay cliente seleccionado
          </h1>
          <p className="font-sans text-brand-gray-500 text-sm mb-6">
            Selecciona un cliente para acceder a tu espacio de asesoría.
          </p>
          <div className="space-y-2">
            {clientesData.map((c) => (
              <button
                key={c.id}
                onClick={() => setClienteId(c.id)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-white border border-brand-gray-200 rounded-xl text-left hover:border-brand-black transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center text-brand-black font-sans text-sm font-bold">
                  {c.nombre.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-sans text-brand-black text-sm font-medium">{c.nombre}</p>
                  <p className="font-sans text-brand-gray-400 text-xs">{c.plan_contratado || 'Sin plan'}</p>
                </div>
                <i className="ri-arrow-right-s-line text-brand-gray-400 ml-auto" />
              </button>
            ))}
          </div>
          {clientesData.length === 0 && (
            <p className="font-sans text-brand-gray-400 text-sm mt-4">
              No hay clientes registrados. Contacta con Anthony para activar tu cuenta.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream">
      <div className="flex">
        <Sidebar
          activeTab={activeTab}
          onChange={setActiveTab}
          badges={sidebarBadges}
          cliente={currentCliente}
          clientes={clientesData}
          onChangeCliente={setClienteId}
        />
        <main className="flex-1 lg:ml-0 pt-14 lg:pt-0">
          <div className="px-4 md:px-8 py-6 md:py-10 max-w-6xl">
            {activeTab === 'dashboard' && (
              <div className="space-y-10">
                <PerfilEstilistico
                  clienteId={clienteId}
                  prendas={prendas}
                  recomendaciones={recomendaciones}
                  calendario={calendario}
                  nombreCliente={currentCliente?.nombre ?? 'Cliente'}
                />
                <div className="border-t border-brand-gray-200" />
                <Dashboard
                  prendas={prendas}
                  recomendaciones={recomendaciones}
                  calendario={calendario}
                  clienteId={clienteId}
                />
              </div>
            )}
            {activeTab === 'looks-sugeridos' && (
              <LooksSugeridos
                clienteId={clienteId}
                prendas={prendas}
                calendario={calendario}
                onAddToCalendar={addLook}
              />
            )}
            {activeTab === 'armario' && (
              <Armario
                prendas={prendas}
                onAdd={addPrenda}
                onUpdate={updatePrenda}
                onRemove={removePrenda}
                onUploadImage={uploadImage}
                loading={loadingPrendas}
              />
            )}
            {activeTab === 'calendario' && (
              <Calendario
                looks={calendario}
                prendas={prendas}
                onAdd={addLook}
                onUpdate={updateLook}
                onRemove={removeLook}
                loading={loadingCalendario}
                clienteId={clienteId}
                error={errorCalendario}
              />
            )}
            {activeTab === 'historial' && (
              <Historial
                looks={calendario}
                prendas={prendas}
                onUpdate={updateLook}
                loading={loadingCalendario}
              />
            )}
            {activeTab === 'combinaciones' && (
              <Combinaciones
                recomendaciones={recomendaciones}
                onMarkAsRead={markAsRead}
                loading={loadingRecomendaciones}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
